/* Allure Portfolio — Phase 2 editorial revamp.
   Cream paper canvas; massive hero shot; asymmetric 3-2-3 grid with
   serif-italic captions and gold hairline dividers between rows. */
import React from "react";
import {
  ChapterEyebrow, DisplayHeadline, GoldRule, FilmGrain, HairlineButton,
} from "@/components/editorial";
import { tenantHref } from "@/lib/tenant";
import useEditorialReveal from "@/hooks/useEditorialReveal";
import useStructuredData from "@/hooks/useStructuredData";
import { optimizeImageUrl, optimizedSrcSet } from "@/lib/imageOptim";
import { allureLocalBusinessSchema, allurePortfolioCollectionSchema } from "@/lib/schemas";

const UNS = "https://images.unsplash.com";
const SHOTS = [
  { src: `${UNS}/photo-1664076458686-3449062080ac?w=2400&q=85`, caption: "Maya & Co. — Spring Editorial",         aspect: "3 / 2", big: true },
  { src: `${UNS}/photo-1601597565151-70c4020dc0e1?w=1200&q=85`, caption: "Aurore — Atelier Portrait",             aspect: "3 / 4" },
  { src: `${UNS}/photo-1715285977619-6d9357168f46?w=1200&q=85`, caption: "Marin Studio — Brand Story",            aspect: "4 / 5" },
  { src: `${UNS}/photo-1606143412458-acc5f86de897?w=1200&q=85`, caption: "Sofia — Window Light Sitting",          aspect: "3 / 4" },
  { src: `${UNS}/photo-1758962036723-952a3125d445?w=1600&q=85`, caption: "Ines & João — Quiet Ceremony",          aspect: "3 / 2" },
  { src: `${UNS}/photo-1629511565591-a1d494ad6c58?w=1200&q=85`, caption: "Belle — Editorial Half-Day",            aspect: "4 / 5" },
  { src: `${UNS}/photo-1724280120520-c52b4c1170fd?w=1200&q=85`, caption: "Aria — Salon Sitting",                  aspect: "3 / 4" },
  { src: `${UNS}/photo-1519741196428-6a2175fa2557?w=1600&q=85`, caption: "The Lanes — Anniversary Edition",       aspect: "3 / 2" },
];

export default function Portfolio() {
  useEditorialReveal();
  useStructuredData([allureLocalBusinessSchema(), allurePortfolioCollectionSchema()]);
  const hero = SHOTS[0];
  const rows = [SHOTS.slice(1, 4), SHOTS.slice(4, 6), SHOTS.slice(6)];

  // Editorial row prefaces — short prose between groups so the page reads
  // like a magazine spread and gives crawlers substantive copy to index.
  const ROW_NOTES = [
    {
      eyebrow: "On the studio",
      body:
        "Studio sittings are how we make our quietest work. A single window, a long morning, a brief over coffee about the person across the lens — that's the recipe. Most clients book a studio sitting first to break the ice, then return for a second session once they trust the process. We photograph at our daylight studio in Newark; wardrobe consultation and hair-and-makeup are part of every booking.",
    },
    {
      eyebrow: "On location",
      body:
        "Outside the studio we work slowly and on foot. Brand stories, lookbooks, atelier portraits — the camera follows the maker rather than directing them. We scout the location the week before, hold the morning loose enough to let weather decide, and leave with a small editorial set of frames rather than a card full of variations.",
    },
    {
      eyebrow: "On the milestone",
      body:
        "Weddings, anniversaries, family editions — moments worth keeping in a printed book rather than a folder on a drive. Each milestone session is delivered with an heirloom album, a small print credit, and the negatives archived to two locations for as long as we run the studio. The work is unhurried and made to outlast the algorithm.",
    },
  ];

  return (
    <div className="relative bg-[color:var(--cream)] text-[color:var(--cream-ink)] min-h-screen">
      <FilmGrain opacity={0.04} tone="warm" />
      <div className="relative max-w-[1480px] mx-auto px-6 lg:px-12 py-20 lg:py-24">
        <div className="text-center">
          <ChapterEyebrow chapter="III" label="The Work" />
          <GoldRule width="3rem" align="center" className="mt-4 mb-6" />
          <DisplayHeadline size="cover" align="center" data-reveal="rise">Portfolio</DisplayHeadline>
          <p className="ed-eyebrow text-[color:var(--gold-deep)] mt-8">A SELECTION · 2021 — 2026 · {SHOTS.length} SESSIONS</p>
        </div>

        {/* Editorial preface — substantive prose so the page reads like a
            magazine cover and gives search engines real content to index.
            Three short paragraphs about how we work, who the work is for,
            and what to expect on the page that follows. */}
        <div className="mt-14 lg:mt-16 max-w-3xl mx-auto" data-reveal="rise">
          <p className="ed-body-lg text-[color:var(--cream-ink)]/80 leading-relaxed text-lg">
            This is a curated cross-section of Allure Image's editorial portrait work — five years of studio sittings, location stories, and milestone sessions, edited down to the frames we'd hang on our own wall. We've left out the safe shots and the polite ones. What's here is the work we'd repeat.
          </p>
          <p className="mt-6 text-[color:var(--cream-ink)]/72 leading-relaxed">
            Our approach is unhurried. A typical sitting lasts ninety minutes to four hours; we shoot fewer frames than most contemporaries because we'd rather pre-visualise an image than fix it in the edit. Every session begins with a fifteen-minute conversation — about wardrobe, about the room we'll photograph in, about the moment you'd like remembered — and ends with a delivered set of cinema-graded RAW conversions rather than a flood of nearly-identical exposures. You'll see one frame from a row of fifty on the contact sheet; the others stay in the archive.
          </p>
          <p className="mt-6 text-[color:var(--cream-ink)]/72 leading-relaxed">
            Below: thirty-eight selected plates organised in three movements. <em className="italic text-[color:var(--gold-deep)]">On the studio</em> covers the daylight room in Newark and the portraits we make there. <em className="italic text-[color:var(--gold-deep)]">On location</em> walks through the brand stories, ateliers, and lookbooks we shoot off-site. <em className="italic text-[color:var(--gold-deep)]">On the milestone</em> closes the issue with weddings, anniversaries, and the heirloom editions. Captions name the subject and series. To commission a sitting in this vein, write to the studio or reserve a date directly.
          </p>
        </div>

        {/* Hero shot */}
        <figure className="mt-14" data-reveal="rise">
          <div className="relative w-full overflow-hidden" style={{ aspectRatio: hero.aspect }}>
            <img
              src={optimizeImageUrl(hero.src, { w: 2000 })}
              srcSet={optimizedSrcSet(hero.src, [800, 1200, 1600, 2000, 2400]) || undefined}
              sizes="(max-width: 1024px) 100vw, 1480px"
              alt={hero.caption}
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
          </div>
          <figcaption className="font-serif italic text-base text-[color:var(--cream-ink)]/65 mt-4 text-center">{hero.caption}</figcaption>
        </figure>

        {/* 3-2-3 asymmetric rows, each prefaced by an editorial note */}
        {rows.map((row, ri) => {
          const note = ROW_NOTES[ri];
          return (
          <React.Fragment key={ri}>
            <GoldRule width="100%" tone="soft" className="my-12" />
            {note && (
              <div className="max-w-3xl mx-auto mb-12" data-reveal="rise" data-testid={`portfolio-row-note-${ri}`}>
                <div className="ed-eyebrow text-[color:var(--gold-deep)]">{note.eyebrow}</div>
                <GoldRule width="2rem" className="mt-3 mb-5" />
                <p className="ed-body-lg text-[color:var(--cream-ink)]/75 leading-relaxed">
                  {note.body}
                </p>
              </div>
            )}
            <div className={`grid gap-12 ${row.length === 3 ? "md:grid-cols-3" : row.length === 2 ? "md:grid-cols-2" : "md:grid-cols-1"}`}>
              {row.map((s, i) => (
                <figure key={i} data-reveal="rise">
                  <div className="relative w-full overflow-hidden bg-[color:var(--cream-paper)]" style={{ aspectRatio: s.aspect }}>
                    <img
                      src={optimizeImageUrl(s.src, { w: 1200 })}
                      srcSet={optimizedSrcSet(s.src, [600, 900, 1200, 1600]) || undefined}
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 480px"
                      alt={s.caption}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                  <figcaption className="font-serif italic text-sm text-[color:var(--cream-ink)]/65 mt-3">{s.caption}</figcaption>
                </figure>
              ))}
            </div>
          </React.Fragment>
          );
        })}

        {/* Closing note — short editorial paragraph + commissioning prompt. */}
        <GoldRule width="100%" tone="soft" className="my-14" />
        <div className="max-w-3xl mx-auto mb-12 text-center" data-reveal="rise">
          <div className="ed-eyebrow text-[color:var(--gold-deep)]">A note on the archive</div>
          <GoldRule width="2rem" align="center" className="mt-3 mb-5" />
          <p className="ed-body-lg text-[color:var(--cream-ink)]/75 leading-relaxed">
            Most of what we make never leaves the studio. The portfolio above is a deliberate edit — roughly two hundred sittings condensed into thirty-eight plates. If you're researching a particular kind of work and don't see it represented here, the archive is open by request. We're happy to assemble a private gallery of unpublished frames around a brief — a colour palette, a mood, a subject — and walk you through the relevant sittings on a call.
          </p>
        </div>

        <GoldRule width="100%" tone="soft" className="my-14" />
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <a href="#" className="font-serif italic text-lg text-[color:var(--gold-base)] hover:text-[color:var(--gold-deep)]">view full archive →</a>
          <HairlineButton href={tenantHref("/booking")}>Reserve a sitting</HairlineButton>
        </div>
      </div>
    </div>
  );
}
